import { useState, useEffect, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X, User, LogOut, Settings, Calendar, Home, Phone, Info, Globe, Moon, Sun } from 'lucide-react';
import { LandingLanguageContext, ThemeContext } from '../App';

interface NavigationProps {
  onOpenAuth?: () => void;
}

export default function Navigation({ onOpenAuth }: NavigationProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const languageContext = useContext(LandingLanguageContext);
  const themeContext = useContext(ThemeContext);
  const theme = themeContext.theme;

  useEffect(() => {
    setIsMenuOpen(false);
    setIsProfileOpen(false);
  }, [location]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsProfileOpen(false);
  };

  const navItems = [
    { icon: Home, label: 'Home', href: '/' },
    { icon: Calendar, label: 'Book Now', href: '/booking' },
    { icon: Phone, label: 'Contact', href: '/contact' },
    { icon: Info, label: 'About', href: '/about-environment' },
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 border-b backdrop-blur-xl ${
        theme === 'dark'
          ? 'border-white/10 bg-[#1a080f]/85 text-[#f8f4ef]'
          : 'border-slate-200/60 bg-white/95 text-slate-900'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-accent-lime-500 text-[#2b0818] font-extrabold text-sm shadow-[0_0_0_2px_rgba(148,215,40,0.25)]">
                FH
              </div>
              <span className={`text-xl font-bold tracking-wide ${theme === 'dark' ? 'text-[#f8f4ef]' : 'text-slate-900'}`}>
                Franco Hotel
              </span>
            </Link>

            <div className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`flex items-center space-x-1 text-sm font-medium transition-colors ${
                    location.pathname === item.href
                      ? 'text-accent-lime-500'
                      : theme === 'dark'
                      ? 'text-[#f8f4ef]/80 hover:text-accent-lime-500'
                      : 'text-slate-600 hover:text-accent-lime-500'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>

            <div className="hidden sm:flex items-center gap-3">
              <div className={`hidden items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold sm:flex ${
                theme === 'dark'
                  ? 'border-white/15 bg-white/10 text-[#f8f4ef]'
                  : 'border-slate-300/80 bg-slate-100/80 text-slate-900'
              }`}>
                <Globe className="h-4 w-4" />
                <button
                  onClick={() => languageContext.setLanguage('en')}
                  className={`rounded-full px-2 py-1 transition ${
                    languageContext.language === 'en'
                      ? 'bg-accent-lime-500 text-[#2b0818]'
                      : 'text-current hover:text-accent-lime-500'
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => languageContext.setLanguage('fr')}
                  className={`rounded-full px-2 py-1 transition ${
                    languageContext.language === 'fr'
                      ? 'bg-accent-lime-500 text-[#2b0818]'
                      : 'text-current hover:text-accent-lime-500'
                  }`}
                >
                  FR
                </button>
              </div>

              <button
                onClick={themeContext.toggleTheme}
                className={`flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-semibold transition ${
                  theme === 'dark'
                    ? 'border-white/15 bg-white/10 text-[#f8f4ef] hover:bg-white/15'
                    : 'border-slate-300/80 bg-slate-100 text-slate-900 hover:bg-slate-200'
                }`}
              >
                {theme === 'dark' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                {theme === 'dark' ? 'Dark' : 'Light'}
              </button>
            </div>

            <div className="flex items-center space-x-4">
              {isAuthenticated && user ? (
                <div className="relative">
                  <button
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className={`flex items-center space-x-2 p-2 rounded-lg transition-colors ${
                      theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-slate-200'
                    }`}
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-primary-600 border border-accent-lime-500/40">
                      <User className="w-4 h-4 text-accent-lime-500" />
                    </div>
                    <span className={`hidden sm:block text-sm font-medium ${theme === 'dark' ? 'text-[#f8f4ef]' : 'text-slate-900'}`}>
                      {user.firstName}
                    </span>
                  </button>

                  {isProfileOpen && (
                    <div className={`absolute right-0 mt-2 w-52 rounded-lg border py-1 shadow-2xl ${theme === 'dark' ? 'border-white/10 bg-slate-800' : 'border-slate-200 bg-white'}`}>
                      <div className={`px-4 py-2 border-b ${theme === 'dark' ? 'border-white/10' : 'border-slate-200'}`}>
                        <p className={`text-sm font-medium ${theme === 'dark' ? 'text-[#f8f4ef]' : 'text-slate-900'}`}>
                          {user.firstName} {user.lastName}
                        </p>
                        <p className={`text-xs ${theme === 'dark' ? 'text-[#f8f4ef]/60' : 'text-slate-600'}`}>{user.email}</p>
                      </div>
                      <Link
                        to="/profile"
                        className={`flex items-center space-x-2 px-4 py-2 text-sm transition ${theme === 'dark' ? 'text-[#f8f4ef]/85 hover:bg-white/10' : 'text-slate-700 hover:bg-slate-100'}`}
                        onClick={() => setIsProfileOpen(false)}
                      >
                        <Settings className="w-4 h-4" />
                        <span>Profile Settings</span>
                      </Link>
                      <Link
                        to="/my-bookings"
                        className={`flex items-center space-x-2 px-4 py-2 text-sm transition ${theme === 'dark' ? 'text-[#f8f4ef]/85 hover:bg-white/10' : 'text-slate-700 hover:bg-slate-100'}`}
                        onClick={() => setIsProfileOpen(false)}
                      >
                        <Calendar className="w-4 h-4" />
                        <span>My Bookings</span>
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="flex items-center space-x-2 px-4 py-2 text-sm text-accent-lime-500 hover:bg-white/10 w-full text-left"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={onOpenAuth}
                  className="bg-accent-lime-500 text-[#2b0818] px-4 py-2 rounded-lg text-sm font-semibold hover:bg-accent-lime-300 transition-colors"
                >
                  Sign In
                </button>
              )}

              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`md:hidden p-2 rounded-lg transition-colors ${
                  theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-slate-200'
                }`}
              >
                {isMenuOpen ? (
                  <X className={`w-6 h-6 ${theme === 'dark' ? 'text-[#f8f4ef]' : 'text-slate-900'}`} />
                ) : (
                  <Menu className={`w-6 h-6 ${theme === 'dark' ? 'text-[#f8f4ef]' : 'text-slate-900'}`} />
                )}
              </button>
            </div>
          </div>
        </div>

        {isMenuOpen && (
          <div className={`md:hidden border-t ${theme === 'dark' ? 'border-white/10 bg-slate-800' : 'border-slate-200/70 bg-white/95'}`}>
            <div className="px-4 py-3 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === item.href
                      ? 'bg-primary-600 text-accent-lime-500'
                      : theme === 'dark'
                      ? 'text-[#f8f4ef]/85 hover:bg-white/10'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              ))}

              {!isAuthenticated && (
                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    onOpenAuth?.();
                  }}
                  className="w-full flex items-center space-x-3 px-3 py-2 bg-accent-lime-500 text-[#2b0818] rounded-lg text-sm font-semibold hover:bg-accent-lime-300"
                >
                  <User className="w-5 h-5" />
                  <span>Sign In</span>
                </button>
              )}
            </div>
          </div>
        )}
      </nav>

      {(isProfileOpen || isMenuOpen) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setIsMenuOpen(false);
            setIsProfileOpen(false);
          }}
        />
      )}
    </>
  );
}
